/**
 * Pure helpers for the bulk-import staging tree.
 *
 * QDex has no folder entities — the tree here is an UPLOAD-TIME scaffold built
 * from each file's relative path. `buildImportTree` turns a flat
 * [{ file, relativePath }] list into a nested folder tree the wizard can render
 * and tag; `flattenForImport` materializes the recursive (inherited) tagging
 * model into the per-file payload the backend /api/import/create expects:
 * a file's effective tags = the union of every ancestor folder's tags ∪ its own.
 *
 * No DOM / File-reading here (see folderUpload.js for that) — keep this pure and
 * unit-testable.
 */

/** Metadata array fields that UNION up the tree (vs. scalars, which override). */
// `projects` was removed in the D1 cutover — project membership is set
// through the project routes now, not inherited from a folder's metadata.
const ARRAY_META_FIELDS = ["authors", "sources", "fileGroups"];

function makeFolder(name, path) {
    return { name, path, tags: [], metadata: {}, folders: new Map(), files: [] };
}

/**
 * Build a nested folder tree from a flat entry list.
 * @param {Array<{file: File, relativePath: string}>} entries
 * @returns {{name,path,tags,metadata,folders:Map,files:Array}} the root folder
 */
export function buildImportTree(entries) {
    const root = makeFolder("", "");
    for (const { file, relativePath } of entries || []) {
        const raw = String(relativePath || (file && file.name) || "").split("/").filter(Boolean);
        if (raw.length === 0) continue;
        const fileName = raw.pop();

        let node = root;
        let pathAcc = "";
        for (const part of raw) {
            pathAcc = pathAcc ? `${pathAcc}/${part}` : part;
            let child = node.folders.get(part);
            if (!child) {
                child = makeFolder(part, pathAcc);
                node.folders.set(part, child);
            }
            node = child;
        }
        node.files.push({ name: fileName, file, tags: [], metadata: {}, include: true });
    }
    return root;
}

/** Case-insensitive de-dupe that preserves order and original casing. */
function mergeStrings(a, b) {
    const seen = new Set();
    const out = [];
    for (const raw of [...(a || []), ...(b || [])]) {
        // Collapse internal whitespace too, mirroring the backend's
        // normalizeTagName, so "Heavy  Metal" and "Heavy Metal" are one tag.
        const value = String(raw).trim().replace(/\s+/g, " ");
        const key = value.toLowerCase();
        if (!value || seen.has(key)) continue;
        seen.add(key);
        out.push(value);
    }
    return out;
}

/**
 * Merge metadata where array fields union (child appended after parent) and
 * scalar fields override (a non-empty child value wins).
 */
function mergeMeta(parent, child) {
    const out = { ...(parent || {}) };
    for (const [key, value] of Object.entries(child || {})) {
        if (ARRAY_META_FIELDS.includes(key)) {
            out[key] = mergeStrings(out[key], Array.isArray(value) ? value : [value]);
        } else if (value !== undefined && value !== "") {
            out[key] = value;
        }
    }
    return out;
}

/**
 * Walk the tree, accumulating inherited tags + metadata, and emit one flat
 * record per INCLUDED file. Folders/files marked `include === false` (and their
 * descendants, for a folder) are skipped.
 *
 * @returns {Array<{name,file,folderPath,tags:string[],metadata:object}>}
 */
export function flattenForImport(root) {
    const out = [];
    const walk = (node, inheritedTags, inheritedMeta) => {
        if (node.include === false) return; // a deselected folder drops its subtree
        const folderTags = mergeStrings(inheritedTags, node.tags);
        const folderMeta = mergeMeta(inheritedMeta, node.metadata);

        for (const f of node.files) {
            if (f.include === false) continue;
            out.push({
                name: f.name,
                file: f.file,
                folderPath: node.path,
                tags: mergeStrings(folderTags, f.tags),
                metadata: mergeMeta(folderMeta, f.metadata),
            });
        }
        for (const child of node.folders.values()) {
            walk(child, folderTags, folderMeta);
        }
    };
    walk(root, [], {});
    return out;
}

/** Count files (optionally only included ones) in a subtree — for UI summaries. */
export function countFiles(node, includedOnly = false) {
    if (!node || node.include === false) return 0;
    let total = 0;
    for (const f of node.files) {
        if (includedOnly && f.include === false) continue;
        total++;
    }
    for (const child of node.folders.values()) {
        total += countFiles(child, includedOnly);
    }
    return total;
}
